import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({ className, classNames, showOutsideDays = false, ...props }: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-2 sm:p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0 justify-center",
        month: "space-y-3 sm:space-y-4 w-full",
        caption: "flex justify-center pt-1 relative items-center mb-2",
        caption_label: "text-sm sm:text-base font-semibold tracking-wide text-ivory",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          "inline-flex items-center justify-center rounded-lg transition-colors",
          "h-9 w-9 sm:h-8 sm:w-8 bg-transparent border border-gold/15 text-ivory/60 hover:text-gold hover:border-gold/30 hover:bg-gold/5",
        ),
        nav_button_previous: "absolute left-0 sm:left-1",
        nav_button_next: "absolute right-0 sm:right-1",
        table: "w-full border-collapse",
        head_row: "flex justify-around",
        head_cell: "text-ivory/40 rounded-md w-10 sm:w-9 font-medium text-[0.7rem] sm:text-[0.8rem] uppercase tracking-wider",
        row: "flex w-full mt-1 justify-around",
        cell: cn(
          "relative text-center text-sm p-0",
          "h-10 w-10 sm:h-9 sm:w-9",
          "focus-within:relative focus-within:z-20",
        ),
        day: cn(
          "inline-flex items-center justify-center rounded-full transition-all",
          "h-10 w-10 sm:h-9 sm:w-9 p-0 font-normal text-sm sm:text-sm",
          "text-ivory/80 hover:bg-gold/10 hover:text-gold",
          "aria-selected:opacity-100",
        ),
        day_range_end: "day-range-end !rounded-full bg-gold text-charcoal font-bold shadow-[0_0_12px_hsl(var(--gold)/0.3)]",
        day_selected:
          "bg-gold text-charcoal hover:bg-gold-glow hover:text-charcoal font-bold shadow-[0_0_12px_hsl(var(--gold)/0.3)]",
        day_today: "text-gold font-semibold ring-1 ring-gold/30",
        day_outside:
          "day-outside text-muted-foreground opacity-20 !bg-transparent hover:!bg-transparent aria-selected:!bg-transparent aria-selected:!text-muted-foreground !border-0 !shadow-none",
        day_disabled: "text-ivory/20 opacity-40 cursor-not-allowed",
        day_range_middle: "aria-selected:bg-gold/10 aria-selected:text-ivory rounded-none aria-selected:hover:bg-gold/15",
        day_range_start: "!rounded-full bg-gold text-charcoal font-bold shadow-[0_0_12px_hsl(var(--gold)/0.3)]",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: ({ ..._props }) => <ChevronLeft className="h-4 w-4" />,
        IconRight: ({ ..._props }) => <ChevronRight className="h-4 w-4" />,
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
